import { useState, useEffect } from 'react';
import TaulukkoFooter from '../../TaulukkoFooter';
import TableLoadingOverlay from './TableLoadingOverlay';

// Pure view, matching the original KloridipitoisuusBlock.jsx table styling
// (same C/CH/inp constants). v1 shows a "Rakenneosa" column when listing
// several kohteet together (allKohteet=true) — that's the permanent mode
// here, since this lists every sample across the whole tarkastuskohde, not
// one rakenneosa tab's worth. No per-row delete/add-extra-row affordance —
// testi rows are only ever created/removed via Poränäyte checking/
// unchecking a sample's testi types.
const C  = { border: '1px solid #adb5bd', padding: '6px 8px', fontSize: '0.9rem', verticalAlign: 'middle' };
const CH = { ...C, background: '#04aa00', color: '#fff', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' };
const inp = { border: '1px solid #ced4da', borderRadius: 3, padding: '3px 6px', fontSize: '0.9rem', width: '100%', background: '#fff' };

// Same mobile card pattern as VetolujuusView.jsx/OhuthieView.jsx — same
// rows-per-sample shape, one row becomes one card below 768px.
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

const cardStyle = { border: '1px solid #e2e4e7', borderRadius: 10, padding: '12px 14px', marginBottom: 12, background: '#fff' };
const cardFieldLabelStyle = { fontSize: '0.72rem', color: '#6c757d', flexShrink: 0, width: 190 };

export default function KloridipitoisuusView({ eligible, rows, loading, addRow, patchArvo, caption, commitCaption, captionKohdeId, taulukkoNum = 1 }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ overflowX: 'auto', position: 'relative', minHeight: loading ? 80 : undefined }}>
      <TableLoadingOverlay show={loading} />
      {eligible.length === 0 ? (
        <div style={{ color: '#6c757d', fontSize: '0.82rem' }}>Ei näytteitä joilla on "Kloridi" valittuna testiksi (Poränäyte-lohko).</div>
      ) : isMobile ? (
        <div>
          {eligible.map(s => {
            const t = (rows[s.id] || [])[0];
            const rakenneosaNimi = s.rakenneosa?.nimi ?? '—';
            if (!t) return (
              <div key={s.id} style={cardStyle}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 6 }}>{rakenneosaNimi} <span style={{ color: '#198754' }}>· {s.nayte_tunnus}</span></div>
                <em style={{ color: '#adb5bd' }}>Ladataan… </em>
                <button type="button" onClick={() => addRow(s)} style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem' }}>Yritä uudelleen</button>
              </div>
            );
            return (
              <div key={t.id} style={cardStyle}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>{rakenneosaNimi} <span style={{ color: '#198754' }}>· {s.nayte_tunnus || '—'}</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={cardFieldLabelStyle}>Kloridipitoisuus betonin painosta (%)</div>
                  <input style={{ ...inp, flex: 1 }} defaultValue={t.arvo?.kloridipitoisuus ?? ''} onBlur={e => patchArvo(t, 'kloridipitoisuus', e.target.value)} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th style={CH}>Rakenneosa</th>
              <th style={CH}>Tunnus</th>
              <th style={CH}>Kloridipitoisuus betonin painosta (%)</th>
            </tr>
          </thead>
          <tbody>
            {eligible.map(s => {
              // Rows auto-create the moment a sample becomes eligible (see
              // useTestiTypeV2) — this only shows for the brief window
              // before that lands, or if creation failed and needs a retry.
              const t = (rows[s.id] || [])[0];
              if (!t) return (
                <tr key={s.id}>
                  <td style={{ ...C, textAlign: 'center' }}>{s.rakenneosa?.nimi ?? '—'}</td>
                  <td style={C}>{s.nayte_tunnus}</td>
                  <td style={C}>
                    <em style={{ color: '#adb5bd' }}>Ladataan… </em>
                    <button type="button" onClick={() => addRow(s)} style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem' }}>Yritä uudelleen</button>
                  </td>
                </tr>
              );
              return (
                <tr key={t.id}>
                  <td style={{ ...C, textAlign: 'center', color: '#495057' }}>{s.rakenneosa?.nimi ?? <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</td>
                  <td style={{ ...C, textAlign: 'center' }}>
                    <span style={{ fontWeight: 500 }}>{s.nayte_tunnus || <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</span>
                  </td>
                  <td style={{ ...C, textAlign: 'center' }}>
                    <input style={{ ...inp, textAlign: 'center' }} defaultValue={t.arvo?.kloridipitoisuus ?? ''} onBlur={e => patchArvo(t, 'kloridipitoisuus', e.target.value)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {/* kohdeId keyed off captionKohdeId (the already-loaded testi_caption
          row), not tkId — see PeitepaksuusView for why. */}
      <TaulukkoFooter taulukkoNum={taulukkoNum} kohdeId={captionKohdeId ?? 'preview'} caption={caption} onChange={commitCaption} />
    </div>
  );
}
